import { ChangeEvent, useEffect } from 'react';
import { TextField, CircularProgress } from '@mui/material';

import { CoreBox, CoreFormControl } from '@youscience/core';

import { useUserProfile } from '@hooks/useUserProfile';
import useRecommenderProfileStore, { SectionName } from '@store/RecommenderProfileStore';
import useCommonAppAdminStore from '@store/CommonAppAdminStore';

import { validateEmail } from '@utils/form';

import { InputMask } from '@components/Form/InputMask';
import SectionAccordion from '@components/SectionAccordion';

import { HELPER_TEXT } from '@constants/optIn';

import ContinueButton from '@components/ContinueButton';

import { StyledCard } from '../../../Card.styles';

import { PersonalDetailsFields, FormState } from '@constants/commonApp/types';
import { SchoolDetailsProps } from '../../types';

const ClEAR_PHONE_NUMBER_LENGTH = 10;

export const formHelperText = {
  firstName: 'First name is required',
  lastName: 'Last name is required',
  emailRequired: 'Email is required',
  emailValidation: 'Please enter a valid email address',
  phoneRequired: 'Please enter a valid phone number',
};

const formatPhoneNumber = (value: string) => {
  return value.replaceAll(/\s|-|(?:\+1)/g, '');
};

export const PersonalDetails = ({
  onExpand,
  onSaveAndContinue,
  isExtended,
}: SchoolDetailsProps<PersonalDetailsFields>) => {
  const {
    initialState: { personalDetails: initialState },
    formState: { personalDetails: formState },
    setInitialData,
    setSectionFormState,
  } = useRecommenderProfileStore((store) => store);

  const { recommenderLastUpdated } = useCommonAppAdminStore((store) => store);

  const { firstName, lastName, phone, email, displayName } = useUserProfile();

  const hasUserData = Boolean(firstName && lastName);

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';
  const areAllRequiredFieldsFilled = Boolean(
    formState.data?.firstName &&
      formState.data?.lastName &&
      formState.data?.email &&
      formState.data?.phone &&
      formatPhoneNumber(formState.data.phone).length === ClEAR_PHONE_NUMBER_LENGTH &&
      validateEmail(formState.data.email),
  );

  const initializeData = () => {
    if (!hasUserData) return;

    const initialValues = {
      fullName: `${firstName} ${lastName}`,
      firstName: firstName || '',
      lastName: lastName || '',
      displayName: displayName || '',
      phone: phone || '',
      email: email || '',
    };

    setInitialData(SectionName.PersonalDetails, initialValues);
  };

  useEffect(() => {
    initializeData();
  }, [recommenderLastUpdated, hasUserData, firstName, lastName, phone, email, displayName]);

  const formatters: { [key: string]: (value: string) => string } = {
    phone: formatPhoneNumber,
  };

  const validate = (formState: FormState<PersonalDetailsFields>) => {
    const errors: {
      [key: string]: string;
    } = {};

    const { data } = formState;

    if (data.firstName.length === 0) {
      errors.firstName = formHelperText.firstName;
    }

    if (data.lastName.length === 0) {
      errors.lastName = formHelperText.lastName;
    }

    if (data.email.length === 0) {
      errors.email = formHelperText.emailRequired;
    } else if (!validateEmail(data.email)) {
      errors.email = formHelperText.emailValidation;
    }

    if (formatPhoneNumber(data.phone).length !== ClEAR_PHONE_NUMBER_LENGTH) {
      errors.phone = formHelperText.phoneRequired;
    }

    return errors;
  };

  const updateFormValues = (fieldUpdates: Partial<PersonalDetailsFields>) => {
    const newFormState = {
      ...formState,
      data: {
        ...formState.data,
        ...fieldUpdates,
      },
    };

    newFormState.data.fullName = `${newFormState.data.firstName} ${newFormState.data.lastName}`;

    const fields = Object.keys(newFormState.data) as Array<keyof PersonalDetailsFields>;
    newFormState.hasChanged = fields.some((field) => {
      if (!initialState) {
        return false;
      }

      if (formatters[field]) {
        return formatters[field](newFormState.data[field]) !== formatters[field](initialState[field]);
      }

      return newFormState.data[field] !== initialState[field];
    });

    newFormState.errors = validate(newFormState);

    newFormState.isFormValid = Object.values(newFormState.errors).every(
      (error) => error?.length === 0 || error === undefined,
    );

    setSectionFormState(SectionName.PersonalDetails, newFormState);
  };

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateFormValues({ [event.target.name]: event.target.value });
  };

  return (
    <SectionAccordion
      title={'Personal Details'}
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      isError={isShowErrors && !areAllRequiredFieldsFilled}
    >
      {!hasUserData ? (
        <StyledCard>
          <CoreBox sx={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <CircularProgress />
          </CoreBox>
        </StyledCard>
      ) : Object.keys(formState.data).length === 0 ? null : (
        <StyledCard>
          <CoreBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1.5rem',
            }}
          >
            <CoreFormControl fullWidth>
              <TextField
                name="firstName"
                label="First name"
                value={formState.data.firstName}
                required
                error={!!formState.errors.firstName}
                helperText={formState.errors.firstName}
                onChange={handleTextFieldChange}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="lastName"
                label="Last name"
                value={formState.data.lastName}
                required
                error={!!formState.errors.lastName}
                helperText={formState.errors.lastName}
                onChange={handleTextFieldChange}
              />
            </CoreFormControl>
          </CoreBox>

          <CoreFormControl sx={{ marginTop: '0.5rem' }}>
            <InputMask
              name="phone"
              label="Phone"
              value={formState.data.phone}
              isRequired
              onChange={handleTextFieldChange}
            />
          </CoreFormControl>

          <CoreFormControl sx={{ marginTop: '0.5rem' }}>
            <TextField
              name="email"
              label="Email"
              value={formState.data.email}
              required
              error={!!formState.errors.email}
              helperText={formState.errors.email}
              onChange={handleTextFieldChange}
            />
          </CoreFormControl>

          <ContinueButton onContinue={onSaveAndContinue} isDisabled={!formState.isFormValid} />
        </StyledCard>
      )}
    </SectionAccordion>
  );
};
